import { useState, useEffect, useRef } from "react";
import { t, font, Button, Avatar, EmptyState, Tabs } from "@realtyflows/ui";
import { getConversations, getMessages, sendMessage, markRead } from "../api";
import useResponsive from "../hooks/useResponsive";

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [msgType, setMsgType] = useState("email");
  const [search, setSearch] = useState("");
  const [showChat, setShowChat] = useState(false);
  const chatEnd = useRef(null);
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  useEffect(() => {
    getConversations()
      .then((convos) => {
        setConversations(convos);
        if (convos.length > 0 && convos[0].client_id) { setActiveConvo(convos[0]); getMessages(convos[0].client_id).then(setChatMessages); }
      }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  useEffect(() => {
    if (!activeConvo?.client_id) return;
    const interval = setInterval(async () => {
      try {
        const msgs = await getMessages(activeConvo.client_id);
        if (msgs.length !== chatMessages.length) {
          setChatMessages(msgs);
          setConversations(await getConversations());
        }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConvo, chatMessages.length]);

  const selectConvo = async (convo) => {
    setActiveConvo(convo);
    if (isMobile) setShowChat(true);
    try {
      setChatMessages(await getMessages(convo.client_id));
      await markRead(convo.client_id);
      setConversations(prev => prev.map(c => c.client_id === convo.client_id ? { ...c, read: true } : c));
    } catch (err) { console.error(err); }
  };

  const handleSend = async () => {
    if (!chatInput.trim() || !activeConvo) return;
    try {
      const msg = await sendMessage({ client_id: activeConvo.client_id, sender: "agent", message: chatInput, type: msgType });
      setChatMessages(prev => [...prev, msg]);
      setChatInput("");
    } catch (err) { console.error(err); }
  };

  const filteredConvos = conversations.filter(c =>
    c.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.client_email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading messages...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", display: isMobile ? "flex" : "grid", gridTemplateColumns: isMobile ? undefined : "320px 1fr", gap: isMobile ? 0 : 16, height: "calc(100vh - 130px)", flexDirection: isMobile ? "column" : undefined }}>
      {/* Conversations List */}
      <div style={{ background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 12, overflow: "hidden", display: isMobile && showChat ? "none" : "flex", flexDirection: "column", flex: isMobile ? 1 : undefined }}>
        <div style={{ padding: "14px 16px", borderBottom: `1.5px solid ${t.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Conversations</span>
            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>{conversations.length} total</span>
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
            style={{ width: "100%", boxSizing: "border-box", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }} />
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {filteredConvos.map(m => (
            <div key={m.id} onClick={() => selectConvo(m)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", gap: 10, cursor: "pointer", background: activeConvo?.client_id === m.client_id ? t.surfaceWarm : "transparent", transition: "background 0.15s" }}
              onMouseEnter={e => { if (activeConvo?.client_id !== m.client_id) e.currentTarget.style.background = t.surfaceWarm; }}
              onMouseLeave={e => { if (activeConvo?.client_id !== m.client_id) e.currentTarget.style.background = "transparent"; }}
            >
              <Avatar name={m.client_name} size={36} />
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{m.client_name}</span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {m.client_email && (
                  <div style={{ fontSize: 10, color: t.emerald, fontFamily: font.mono, marginTop: 1 }}>{m.client_email}</div>
                )}
                <div style={{ fontSize: 11, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2, fontFamily: font.body }}>{m.message}</div>
              </div>
              {!m.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.emerald, flexShrink: 0, marginTop: 4 }} />}
            </div>
          ))}
          {filteredConvos.length === 0 && (
            <EmptyState title="No conversations found" sub="Start a conversation with a client" />
          )}
        </div>
      </div>

      {/* Chat Panel */}
      <div style={{ background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 12, display: isMobile && !showChat ? "none" : "flex", flexDirection: "column", flex: isMobile ? 1 : undefined }}>
        {activeConvo ? (
          <>
            <div style={{ padding: isMobile ? "12px 14px" : "14px 20px", borderBottom: `1.5px solid ${t.border}`, display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
              {isMobile && (
                <div
                  onClick={() => setShowChat(false)}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: t.surfaceWarm, border: `1px solid ${t.border}`, flexShrink: 0 }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 12L6 8L10 4" stroke={t.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              <Avatar name={activeConvo.client_name} size={isMobile ? 32 : 36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{activeConvo.client_name}</div>
                {activeConvo.client_email && (
                  <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{activeConvo.client_email}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ fontSize: 11, color: t.amber, background: t.amberLight, padding: "4px 12px", borderRadius: 99, cursor: "pointer", border: `1px solid ${t.amber}40`, fontFamily: font.body }}>Take Over</div>
              </div>
            </div>

            {/* AI Status Bar */}
            <div style={{ margin: isMobile ? "10px 12px 0" : "12px 20px 0", background: t.emeraldLight, border: `1px solid ${t.emerald}20`, borderRadius: 8, padding: isMobile ? "6px 10px" : "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 12, color: t.emerald, fontWeight: 700, fontFamily: font.body }}>AI</div>
              <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body }}>AI is qualifying this client. Messages are sent via email.</div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: "auto", padding: isMobile ? 12 : 20, display: "flex", flexDirection: "column", gap: 12 }}>
              {chatMessages.map((m, i) => (
                <div key={m.id || i} style={{ display: "flex", justifyContent: m.sender === "client" ? "flex-start" : m.sender === "agent" ? "flex-end" : "flex-start", gap: 8 }}>
                  {m.sender === "ai" && (
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: t.emeraldLight, border: `1px solid ${t.emerald}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: t.emerald, fontWeight: 700, flexShrink: 0, fontFamily: font.body }}>AI</div>
                  )}
                  {m.sender === "client" && (
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: t.surfaceWarm, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: t.textSecondary, fontWeight: 700, flexShrink: 0, fontFamily: font.mono }}>
                      {activeConvo.client_avatar || "?"}
                    </div>
                  )}
                  <div>
                    <div style={{
                      maxWidth: isMobile ? "85%" : "70%", borderRadius: m.sender === "agent" ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                      padding: "10px 14px", fontSize: 13, lineHeight: 1.5, fontFamily: font.body,
                      background: m.sender === "ai" ? t.surfaceWarm : m.sender === "agent" ? t.emerald : m.sender === "client" ? t.surfaceWarm : t.emerald,
                      color: m.sender === "ai" ? t.textPrimary : m.sender === "agent" ? "#fff" : m.sender === "client" ? t.textPrimary : "#fff",
                      border: (m.sender === "ai" || m.sender === "client") ? `1px solid ${t.border}` : "none",
                    }}>
                      {m.message}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 9, color: t.textMuted }}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span style={{ fontSize: 9, color: t.textMuted, fontFamily: font.body }}>
                        {m.sender === "agent" ? "You" : m.sender === "ai" ? "AI Bot" : activeConvo.client_name}
                      </span>
                      {m.type && (
                        <span style={{ fontSize: 8, color: t.emeraldDeep, background: t.emeraldLight, padding: "1px 6px", borderRadius: 4, fontWeight: 600, fontFamily: font.body }}>
                          {m.type.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatEnd} />
            </div>

            {/* Message Input */}
            <div style={{ padding: isMobile ? "10px 12px" : "14px 20px", borderTop: `1.5px solid ${t.border}` }}>
              <div style={{ display: "flex", gap: 6, marginBottom: isMobile ? 8 : 10 }}>
                <Tabs
                  tabs={["email", "chat"]}
                  activeKey={msgType}
                  onChange={setMsgType}
                  variant="pill"
                  size="sm"
                />
              </div>
              <div style={{ display: "flex", gap: isMobile ? 8 : 10 }}>
                <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSend()}
                  placeholder={`Type a ${msgType} message...`}
                  style={{ flex: 1, minWidth: 0, background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8, padding: isMobile ? "10px 12px" : "10px 14px", color: t.textPrimary, fontSize: 13, outline: "none", fontFamily: font.body }} />
                <Button variant="primary" size="sm" onClick={handleSend}>Send</Button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, flexDirection: "column", gap: 12 }}>
            <div style={{ color: t.textMuted, fontSize: 14, fontFamily: font.body }}>Select a conversation</div>
            <div style={{ color: t.borderDark, fontSize: 12, fontFamily: font.body }}>
              All messages are sent via email
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
